import { motion } from "framer-motion";
import {
  Share2,
  Twitter,
  Facebook,
  Linkedin,
  Link2,
  Check,
  MessageCircle,
} from "lucide-react";
import { useState, useEffect } from "react";
import { useLocale } from "../context/LocaleContext";
import api from "../services/api";

interface ShareButtonProps {
  title: string;
  url: string;
  description?: string;
}

interface ShareTexts {
  shareButton: string;
  shareTitle: string;
  shareOnTwitter: string;
  shareOnFacebook: string;
  shareOnLinkedin: string;
  shareOnWhatsapp: string;
  copyLink: string;
  linkCopied: string;
}

export function ShareButton({ title, url, description }: ShareButtonProps) {
  const [showMenu, setShowMenu] = useState(false);
  const [copied, setCopied] = useState(false);
  const { locale } = useLocale();
  const [shareTexts, setShareTexts] = useState<ShareTexts>({
    shareButton: "Share",
    shareTitle: "Share this article",
    shareOnTwitter: "Share on Twitter",
    shareOnFacebook: "Share on Facebook",
    shareOnLinkedin: "Share on LinkedIn",
    shareOnWhatsapp: "Share on WhatsApp",
    copyLink: "Copy link",
    linkCopied: "Link copied!",
  });

  useEffect(() => {
    async function fetchShareTexts() {
      try {
        const response = await api.getGlobal(locale);
        if (response?.data?.share_button) {
          setShareTexts(response.data.share_button);
        }
      } catch (error) {
        console.error("Failed to fetch share button texts:", error);
      }
    }
    fetchShareTexts();
  }, [locale]);

  // Use production URL or fallback to current origin
  const baseUrl =
    process.env.REACT_APP_PUBLIC_URL ||
    (window.location.hostname === "localhost"
      ? "https://devops.caglayagmur.dev"
      : window.location.origin);
  const fullUrl = `${baseUrl}${url}`;
  const shareText = `${title} by Cagla Yagmur - ${fullUrl}`;

  const shareLinks = {
    twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(
      shareText
    )}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
      fullUrl
    )}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
      fullUrl
    )}`,
    whatsapp: `https://wa.me/?text=${encodeURIComponent(shareText)}`,
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy link:", err);
    }
  };

  const handleShare = (platform: keyof typeof shareLinks) => {
    window.open(
      shareLinks[platform],
      "_blank",
      "noopener,noreferrer,width=600,height=400"
    );
    setShowMenu(false);
  };

  return (
    <div className="relative">
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setShowMenu(!showMenu)}
        className="flex items-center gap-2 px-4 py-2 bg-gray-900/50 border border-red-900/20 rounded-lg hover:border-red-600/50 transition-colors"
      >
        <Share2 className="w-5 h-5" />
        <span>{shareTexts.shareButton}</span>
      </motion.button>

      {showMenu && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setShowMenu(false)}
          />

          {/* Menu */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute top-full left-0 mt-2 z-50 w-64 bg-gray-900 border border-red-900/20 rounded-xl shadow-xl overflow-hidden"
          >
            <div className="p-3 border-b border-red-900/20">
              <p className="text-sm font-semibold text-white">
                {shareTexts.shareTitle}
              </p>
            </div>

            <div className="p-2">
              <button
                onClick={() => handleShare("twitter")}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-800 transition-colors group"
              >
                <div className="w-8 h-8 rounded-full bg-blue-500/10 flex items-center justify-center group-hover:bg-blue-500/20 transition-colors">
                  <Twitter className="w-4 h-4 text-blue-500" />
                </div>
                <span className="text-sm text-gray-300">
                  {shareTexts.shareOnTwitter}
                </span>
              </button>

              <button
                onClick={() => handleShare("facebook")}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-800 transition-colors group"
              >
                <div className="w-8 h-8 rounded-full bg-blue-600/10 flex items-center justify-center group-hover:bg-blue-600/20 transition-colors">
                  <Facebook className="w-4 h-4 text-blue-600" />
                </div>
                <span className="text-sm text-gray-300">
                  {shareTexts.shareOnFacebook}
                </span>
              </button>

              <button
                onClick={() => handleShare("linkedin")}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-800 transition-colors group"
              >
                <div className="w-8 h-8 rounded-full bg-blue-700/10 flex items-center justify-center group-hover:bg-blue-700/20 transition-colors">
                  <Linkedin className="w-4 h-4 text-blue-700" />
                </div>
                <span className="text-sm text-gray-300">
                  {shareTexts.shareOnLinkedin}
                </span>
              </button>

              <button
                onClick={() => handleShare("whatsapp")}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-800 transition-colors group"
              >
                <div className="w-8 h-8 rounded-full bg-green-500/10 flex items-center justify-center group-hover:bg-green-500/20 transition-colors">
                  <MessageCircle className="w-4 h-4 text-green-500" />
                </div>
                <span className="text-sm text-gray-300">
                  {shareTexts.shareOnWhatsapp}
                </span>
              </button>

              <div className="my-2 border-t border-red-900/20" />

              <button
                onClick={handleCopyLink}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-800 transition-colors group"
              >
                <div className="w-8 h-8 rounded-full bg-red-600/10 flex items-center justify-center group-hover:bg-red-600/20 transition-colors">
                  {copied ? (
                    <Check className="w-4 h-4 text-green-500" />
                  ) : (
                    <Link2 className="w-4 h-4 text-red-600" />
                  )}
                </div>
                <span className="text-sm text-gray-300">
                  {copied ? shareTexts.linkCopied : shareTexts.copyLink}
                </span>
              </button>
            </div>
          </motion.div>
        </>
      )}
    </div>
  );
}
